import { gql } from "@apollo/client";
import MultiValuesField from "controls/MultiValuesField";
import sameOrNull from "helpers/sameOrNull";
import useData from "hooks/useData";
import DateInput from "inputs/DateInput";
import PasswordInput from "inputs/PasswordInput";
import SwitchInput from "inputs/SwitchInput";
import TextInput, { IpAdddressInput, MacAddressInput } from "inputs/TextInput";
import React, { useState } from "react";
import ReadminObjectInput from "readmin_pages/shared/ReadminObjectInput";

export const CamerasFormFragment = gql`
  fragment CamerasFormFragment on Camera {
    id
    cameraId
    enabled
    cameraModel {
      id
    }
    venueServer {
      id
    }
    ipAddress
    macAddress
    details
    purchaseDate
    allowLiveStreams
    deviceUsername
    devicePassword
    captureH265
    captureFfmpegArgs
  }
`;

export default function CamerasForm({ cameras }) {
  cameras = cameras.map((camera) => ({
    enabled: true,
    captureH265: true, // added default value true and combine with cameraModel.supportsH265 to ensure we always use H265 where possible
    ...camera,
  }));

  const [cameraModels, cameraModelsSet] = useState(() => cameras.map((camera) => camera.cameraModel));
  const cameraModel = sameOrNull(cameraModels);
  const [data] = useData(
    gql`
      query CamerasForm($cameraModelId: ID) {
        cameraModel(id: $cameraModelId) {
          id
          supportsH265
        }
      }
    `,
    { cameraModelId: cameraModel?.id },
  );

  return (
    <>
      <MultiValuesField
        label="Camera ID"
        name="cameraId"
        required
        defaultValues={cameras?.map((camera) => camera.cameraId)}
        input={<TextInput inputProps={{ pattern: "cam-.+" }} />}
        helperText={<>cam-venue-identifier e.g. cam-ags-a</>}
      />
      <MultiValuesField
        name="venueServer"
        label="Venue Server"
        defaultValues={cameras?.map((camera) => camera.venueServer)}
        input={<ReadminObjectInput optionsTypename="VenueServer" />}
      />
      <MultiValuesField
        name="ipAddress"
        label="IP Address"
        required
        defaultValues={cameras?.map((camera) => camera.ipAddress)}
        input={<IpAdddressInput />}
        helperText={
          <>
            E.g 10.9.9.1
            <br />
            Should be within Venue Server&apos;s internal network range. Normally numbered by court number.
          </>
        }
      />
      <MultiValuesField
        name="macAddress"
        label="MAC Address"
        defaultValues={cameras?.map((camera) => camera.macAddress)}
        input={<MacAddressInput />}
      />
      <MultiValuesField
        name="deviceUsername"
        label="Device Username"
        defaultValues={cameras?.map((camera) => camera.deviceUsername)}
        input={<TextInput />}
        helperText={
          <>
            For Axis cameras, default username is <code>root</code>
          </>
        }
      />
      <MultiValuesField
        name="devicePassword"
        label="Device Password"
        defaultValues={cameras?.map((camera) => camera.devicePassword)}
        input={<PasswordInput />}
      />
      <MultiValuesField
        name="cameraModel"
        label="Model"
        required
        values={cameraModels}
        onChange={cameraModelsSet}
        input={<ReadminObjectInput optionsTypename="CameraModel" />}
      />
      <MultiValuesField
        name="purchaseDate"
        label="Purchase Date"
        defaultValues={cameras?.map((camera) => camera.purchaseDate)}
        input={<DateInput />}
        helperText={<>Date the camera was purchased.</>}
      />
      <MultiValuesField
        name="details"
        label="Details"
        defaultValues={cameras?.map((camera) => camera.details)}
        input={<TextInput multiline minRows={5} />}
      />
      <MultiValuesField
        label="Enabled"
        name="enabled"
        defaultValues={cameras?.map((camera) => camera.enabled)}
        input={<SwitchInput />}
        helperText={<>Enables video processing.</>}
      />
      <MultiValuesField
        label="Allow live streams"
        name="allowLiveStreams"
        defaultValues={cameras?.map((camera) => camera.allowLiveStreams)}
        input={<SwitchInput />}
      />
      {data?.cameraModel && (
        <MultiValuesField
          label="Capture H265"
          name="captureH265"
          // Disable if camera model does not support H265
          disabled={!data?.cameraModel?.supportsH265}
          // Default to true if supported, otherwise false
          defaultValues={cameras?.map((camera) => (data?.cameraModel?.supportsH265 ? !!camera.captureH265 : false))}
          input={<SwitchInput />}
          helperText={
            <>Capture H265 from the camera, reduce bandwidth and storage. Should always be ticked when available.</>
          }
        />
      )}
      <MultiValuesField
        label="Capture FFmpeg args"
        name="captureFfmpegArgs"
        defaultValues={cameras?.map((camera) => camera.captureFfmpegArgs)}
        input={<TextInput multiline />}
        helperText={
          <>
            Custom FFmpeg args to use when capturing video from the camera. One arg per line. Should usually be left
            blank for normal cameras.
          </>
        }
      />
    </>
  );
}
